/**
 * Badge colorido para status de ProvisioningJob (TASK-2436 + REDESIGN 2026-04-26).
 *
 * Mapeamento (com pulse animation quando running):
 *   pending   → gray
 *   running   → blue (pulse)
 *   completed → teal
 *   failed    → red
 *   cancelled → orange
 *   skipped   → gray
 */

import { Badge, type BadgeProps } from '@mantine/core'

const STATUS_CONFIG: Record<string, { label: string; color: string; pulse: boolean }> = {
  pending: { label: 'Pendente', color: 'gray', pulse: false },
  running: { label: 'Provisionando', color: 'blue', pulse: true },
  completed: { label: 'Concluído', color: 'teal', pulse: false },
  failed: { label: 'Falhou', color: 'red', pulse: false },
  cancelled: { label: 'Cancelado', color: 'orange', pulse: false },
  skipped: { label: 'Pulado', color: 'gray', pulse: false },
}

interface Props extends Omit<BadgeProps, 'color'> {
  status: string
  withDot?: boolean
}

const PULSE_KEYFRAMES = `
@keyframes job-status-pulse {
  0%   { box-shadow: 0 0 0 0 currentColor; opacity: 1; }
  70%  { box-shadow: 0 0 0 8px transparent; opacity: 0.55; }
  100% { box-shadow: 0 0 0 0 transparent; opacity: 1; }
}
`

export function JobStatusBadge({ status, withDot = true, ...rest }: Props) {
  const cfg = STATUS_CONFIG[status] ?? { label: status, color: 'gray', pulse: false }

  const dot = (
    <span
      data-testid={`job-status-dot-${status}`}
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: `var(--mantine-color-${cfg.color}-6)`,
        color: `var(--mantine-color-${cfg.color}-6)`,
        marginRight: 8,
        animation: cfg.pulse ? 'job-status-pulse 1.6s ease-out infinite' : undefined,
      }}
    />
  )

  return (
    <>
      {cfg.pulse && <style>{PULSE_KEYFRAMES}</style>}
      <Badge
        color={cfg.color}
        variant="light"
        size="lg"
        radius="sm"
        leftSection={withDot ? dot : undefined}
        data-testid="job-status-badge"
        styles={{
          root: { letterSpacing: 0.4, textTransform: 'none', fontWeight: 600 },
        }}
        {...rest}
      >
        {cfg.label}
      </Badge>
    </>
  )
}
